import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFile, 
  faFilePdf, 
  faImage, 
  faCheck, 
  faTimes, 
  faEye,
  faSpinner,
  faExclamationTriangle,
  faClock,
  faUserGraduate,
  faFilter,
  faCheckDouble,
  faSearch,
  faSquare,
  faCheckSquare,
  faMinusSquare,
  faChevronDown,
  faChevronUp,
  faSync
} from '@fortawesome/free-solid-svg-icons';
import { documentoAlunoService, TIPOS_DOCUMENTO } from '../../services/documentoAlunoService';
import { useAuth } from '../../contexts/AuthContext';
import Pagination from '../../components/Pagination';
import api from '../../services/api';

const DocumentosPendentes = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const nivelAcesso = user?.nivelAcesso || user?.perfil?.nivelAcesso || user?.nivel_acesso || 0;
  
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Filtros
  const [showFilters, setShowFilters] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroExtensao, setFiltroExtensao] = useState('');
  const [busca, setBusca] = useState('');
  const [extensoes, setExtensoes] = useState([]);
  
  // Seleção múltipla
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Modais e estados
  const [viewingDocument, setViewingDocument] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [documentoToReject, setDocumentoToReject] = useState(null);
  const [motivoRejeicao, setMotivoRejeicao] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showBatchConfirm, setShowBatchConfirm] = useState(false);
  const [batchAction, setBatchAction] = useState(null);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });

  // Verificar permissão
  useEffect(() => {
    if (nivelAcesso <= 3) {
      navigate('/dashboard');
    }
  }, [nivelAcesso, navigate]);

  // Carregar extensões para filtro
  useEffect(() => {
    const loadExtensoes = async () => {
      try {
        const response = await api.get('/extensoes', { params: { limit: 1000 } });
        setExtensoes(response.data?.data || response.data || []);
      } catch (err) {
        console.error('Erro ao carregar extensões:', err);
      }
    };
    loadExtensoes();
  }, []);

  // Carregar documentos
  const loadDocumentos = useCallback(async () => {
    setLoading(true);
    setSelectedIds(new Set());
    setSelectAll(false);
    try {
      const result = await documentoAlunoService.getDocumentosPendentes({ 
        page, 
        limit,
        tipo: filtroTipo || undefined,
        extensao: filtroExtensao || undefined,
        busca: busca || undefined
      });
      if (result.success) {
        setDocumentos(result.data || []);
        setTotalPages(result.pagination?.totalPages || 1);
        setTotal(result.pagination?.total || 0);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Erro ao carregar documentos pendentes');
    } finally {
      setLoading(false);
    }
  }, [page, limit, filtroTipo, filtroExtensao, busca]);

  useEffect(() => {
    loadDocumentos();
  }, [loadDocumentos]);

  // Limpar mensagens
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Seleção de documentos
  const handleSelectDocument = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
    setSelectAll(newSelected.size === documentos.length && documentos.length > 0);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds(new Set());
      setSelectAll(false);
    } else {
      setSelectedIds(new Set(documentos.map(d => d.idDocumento)));
      setSelectAll(true);
    }
  };

  // Aprovar individual
  const handleAprovar = async (documento) => {
    setProcessing(true);
    try {
      const result = await documentoAlunoService.aprovarDocumento(documento.idDocumento);
      if (result.success) {
        setSuccess(`Documento aprovado com sucesso!`);
        await loadDocumentos();
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Erro ao aprovar documento');
    } finally {
      setProcessing(false);
    }
  };

  // Rejeitar individual
  const handleRejeitar = async () => {
    if (!documentoToReject || !motivoRejeicao.trim()) {
      setError('Informe o motivo da rejeição');
      return;
    }
    
    setProcessing(true);
    try {
      const result = await documentoAlunoService.rejeitarDocumento(documentoToReject.idDocumento, motivoRejeicao);
      if (result.success) {
        setSuccess('Documento rejeitado');
        await loadDocumentos();
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Erro ao rejeitar documento');
    } finally {
      setShowRejectModal(false);
      setDocumentoToReject(null);
      setMotivoRejeicao('');
      setProcessing(false);
    }
  };

  // Aprovação em lote
  const handleBatchAprovar = async () => {
    if (selectedIds.size === 0) return;
    
    setProcessing(true);
    setBatchProgress({ current: 0, total: selectedIds.size });
    
    let successCount = 0;
    let errorCount = 0;
    const ids = Array.from(selectedIds);
    
    for (let i = 0; i < ids.length; i++) {
      try {
        const result = await documentoAlunoService.aprovarDocumento(ids[i]);
        if (result.success) successCount++;
        else errorCount++;
      } catch (err) {
        errorCount++;
      }
      setBatchProgress({ current: i + 1, total: ids.length });
    }
    
    setShowBatchConfirm(false);
    setBatchAction(null);
    setProcessing(false);
    
    if (successCount > 0) setSuccess(`${successCount} documento(s) aprovado(s) com sucesso!`);
    if (errorCount > 0) setError(`${errorCount} documento(s) não puderam ser aprovados.`);
    
    await loadDocumentos();
  };

  // Rejeição em lote
  const handleBatchRejeitar = async () => {
    if (selectedIds.size === 0 || !motivoRejeicao.trim()) {
      setError('Informe o motivo da rejeição');
      return;
    }
    
    setProcessing(true);
    setBatchProgress({ current: 0, total: selectedIds.size });
    
    let successCount = 0;
    let errorCount = 0;
    const ids = Array.from(selectedIds);
    
    for (let i = 0; i < ids.length; i++) {
      try {
        const result = await documentoAlunoService.rejeitarDocumento(ids[i], motivoRejeicao);
        if (result.success) successCount++;
        else errorCount++;
      } catch (err) {
        errorCount++;
      }
      setBatchProgress({ current: i + 1, total: ids.length });
    }
    
    setShowBatchConfirm(false);
    setBatchAction(null);
    setMotivoRejeicao('');
    setProcessing(false);
    
    if (successCount > 0) setSuccess(`${successCount} documento(s) rejeitado(s)!`);
    if (errorCount > 0) setError(`${errorCount} documento(s) não puderam ser rejeitados.`);
    
    await loadDocumentos();
  };

  const handleViewDocument = async (documento) => {
    try {
      const blobUrl = await documentoAlunoService.getDocumentoBlob(documento.nomeArquivo);
      if (blobUrl) {
        setViewingDocument({ ...documento, blobUrl });
      } else {
        setError('Erro ao carregar documento');
      }
    } catch (err) {
      setError('Erro ao visualizar documento');
    }
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.includes('pdf')) return <FontAwesomeIcon icon={faFilePdf} className="text-red-500" />;
    if (mimeType?.startsWith('image/')) return <FontAwesomeIcon icon={faImage} className="text-blue-500" />;
    return <FontAwesomeIcon icon={faFile} className="text-gray-500" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
  };

  const clearFilters = () => {
    setFiltroTipo('');
    setFiltroExtensao('');
    setBusca('');
    setPage(1);
  };

  const getStats = () => {
    const stats = {};
    documentos.forEach(doc => {
      const tipo = doc.tipoDocumento;
      stats[tipo] = (stats[tipo] || 0) + 1;
    });
    return stats;
  };

  if (nivelAcesso <= 3) return null;

  const stats = getStats();
  const hasFilters = filtroTipo || filtroExtensao || busca;

  return (
    <div className="w-full bg-white rounded shadow p-4 sm:p-6 mt-4 sm:mt-6">
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
        <div className="flex items-center gap-3">
          <FontAwesomeIcon icon={faClock} className="text-xl sm:text-2xl text-yellow-500" />
          <div>
            <h2 className="text-lg sm:text-xl font-bold">Documentos Pendentes de Revisão</h2>
            <p className="text-gray-500 text-xs sm:text-sm">{total} documento(s) aguardando aprovação</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={loadDocumentos}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded"
            title="Atualizar lista"
            disabled={loading}
          >
            <FontAwesomeIcon icon={faSync} spin={loading} />
          </button>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`px-3 py-2 rounded flex items-center gap-2 text-sm ${
              showFilters || hasFilters ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <FontAwesomeIcon icon={faFilter} />
            <span className="hidden sm:inline">Filtros</span>
            {hasFilters && <span className="bg-blue-500 text-white text-xs px-1.5 rounded-full">!</span>}
            <FontAwesomeIcon icon={showFilters ? faChevronUp : faChevronDown} className="text-xs" />
          </button>
        </div>
      </div>

      {/* Painel de Filtros */}
      {showFilters && (
        <div className="mb-4 p-4 bg-gray-50 border rounded-lg">
          <form onSubmit={handleSearch} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Buscar aluno</label>
                <div className="relative">
                  <input
                    type="text"
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    placeholder="Nome ou matrícula..."
                    className="w-full border rounded px-3 py-2 text-sm pr-8"
                  />
                  <FontAwesomeIcon icon={faSearch} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Tipo de documento</label>
                <select
                  value={filtroTipo}
                  onChange={(e) => { setFiltroTipo(e.target.value); setPage(1); }}
                  className="w-full border rounded px-3 py-2 text-sm"
                >
                  <option value="">Todos os tipos</option>
                  {TIPOS_DOCUMENTO.map((tipo) => (
                    <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Extensão</label>
                <select
                  value={filtroExtensao}
                  onChange={(e) => { setFiltroExtensao(e.target.value); setPage(1); }}
                  className="w-full border rounded px-3 py-2 text-sm"
                >
                  <option value="">Todas as extensões</option>
                  {extensoes.map((ext) => (
                    <option key={ext.idExtensao || ext.id} value={ext.idExtensao || ext.id}>{ext.nome}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Itens por página</label>
                <select
                  value={limit}
                  onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                  className="w-full border rounded px-3 py-2 text-sm"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
            
            <div className="flex items-center gap-2 pt-2">
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm flex items-center gap-2">
                <FontAwesomeIcon icon={faSearch} />
                Buscar
              </button>
              {hasFilters && (
                <button type="button" onClick={clearFilters} className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm">
                  Limpar filtros
                </button>
              )}
            </div>
          </form>
          
          {Object.keys(stats).length > 0 && (
            <div className="mt-3 pt-3 border-t">
              <p className="text-xs text-gray-500 mb-2">Documentos nesta página por tipo:</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(stats).map(([tipo, count]) => (
                  <span 
                    key={tipo}
                    className="px-2 py-1 bg-white border rounded text-xs cursor-pointer hover:bg-gray-100"
                    onClick={() => { setFiltroTipo(tipo); setPage(1); }}
                  >
                    {documentoAlunoService.getTipoLabel(tipo)}: <strong>{count}</strong>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Barra de ações em lote */}
      {documentos.length > 0 && (
        <div className="mb-4 p-3 bg-gray-100 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <button type="button" onClick={handleSelectAll} className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900">
              <FontAwesomeIcon 
                icon={selectAll ? faCheckSquare : (selectedIds.size > 0 ? faMinusSquare : faSquare)} 
                className={selectAll || selectedIds.size > 0 ? 'text-blue-600' : 'text-gray-400'}
              />
              <span>{selectAll ? 'Desmarcar todos' : 'Selecionar todos'}</span>
            </button>
            
            {selectedIds.size > 0 && (
              <span className="text-sm text-blue-600 font-medium">{selectedIds.size} selecionado(s)</span>
            )}
          </div>
          
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => { setBatchAction('aprovar'); setShowBatchConfirm(true); }}
                className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm flex items-center gap-2"
                disabled={processing}
              >
                <FontAwesomeIcon icon={faCheckDouble} />
                <span>Aprovar {selectedIds.size}</span>
              </button>
              <button
                type="button"
                onClick={() => { setBatchAction('rejeitar'); setShowBatchConfirm(true); }}
                className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm flex items-center gap-2"
                disabled={processing}
              >
                <FontAwesomeIcon icon={faTimes} />
                <span>Rejeitar {selectedIds.size}</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Mensagens de feedback */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 flex-shrink-0" />
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      )}
      
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
          <FontAwesomeIcon icon={faCheck} className="text-green-500 flex-shrink-0" />
          <span className="text-green-700 text-sm">{success}</span>
        </div>
      )}

      {/* Lista de documentos */}
      {loading ? (
        <div className="flex items-center justify-center py-12 sm:py-16 text-gray-500">
          <FontAwesomeIcon icon={faSpinner} spin className="mr-2 text-xl sm:text-2xl" />
          <span className="text-sm sm:text-base">Carregando documentos pendentes...</span>
        </div>
      ) : documentos.length === 0 ? (
        <div className="text-center py-12 sm:py-16 text-gray-500 bg-gray-50 rounded-lg">
          <FontAwesomeIcon icon={faCheck} className="text-4xl sm:text-6xl text-green-400 mb-4" />
          <p className="text-base sm:text-lg font-medium">
            {hasFilters ? 'Nenhum documento encontrado com os filtros aplicados' : 'Nenhum documento pendente de revisão!'}
          </p>
          <p className="text-xs sm:text-sm">
            {hasFilters ? 'Tente ajustar os filtros de busca' : 'Todos os documentos já foram processados.'}
          </p>
          {hasFilters && (
            <button type="button" onClick={clearFilters} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">
              Limpar filtros
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {documentos.map((documento) => (
              <div 
                key={documento.idDocumento}
                className={`p-3 sm:p-4 border rounded-lg hover:shadow-sm transition-shadow ${
                  selectedIds.has(documento.idDocumento) ? 'bg-blue-50 border-blue-300' : 'bg-gray-50'
                }`}
              >
                <div className="flex flex-col gap-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <button type="button" onClick={() => handleSelectDocument(documento.idDocumento)} className="flex-shrink-0 p-1">
                        <FontAwesomeIcon 
                          icon={selectedIds.has(documento.idDocumento) ? faCheckSquare : faSquare} 
                          className={selectedIds.has(documento.idDocumento) ? 'text-blue-600 text-lg' : 'text-gray-400 text-lg'}
                        />
                      </button>
                      <div className="text-2xl sm:text-3xl flex-shrink-0">{getFileIcon(documento.mimeType)}</div>
                      <p className="font-bold text-gray-800 text-sm sm:text-base">
                        {documentoAlunoService.getTipoLabel(documento.tipoDocumento)}
                      </p>
                    </div>
                    <div className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 flex items-center gap-1 flex-shrink-0">
                      <FontAwesomeIcon icon={faClock} className="text-xs" />
                      <span className="hidden sm:inline">Aguardando</span>
                    </div>
                  </div>
                  
                  <div className="text-sm text-gray-600 space-y-1 ml-8 sm:ml-10">
                    <div className="flex items-center gap-1 flex-wrap">
                      <FontAwesomeIcon icon={faUserGraduate} className="text-gray-400 text-xs" />
                      <span className="font-medium">{documento.nomeAluno}</span>
                      {documento.matricula && <span className="text-gray-500">({documento.matricula})</span>}
                    </div>
                    {documento.nomeExtensao && <p className="text-xs text-gray-500">{documento.nomeExtensao}</p>}
                    <p className="text-xs text-gray-500">Enviado: {formatDate(documento.dataUpload)}</p>
                  </div>
                  
                  <div className="flex items-center gap-2 pt-2 border-t border-gray-200 ml-8 sm:ml-10">
                    <button
                      type="button"
                      onClick={() => handleViewDocument(documento)}
                      className="flex-1 sm:flex-none px-3 py-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded flex items-center justify-center gap-1 text-sm"
                      disabled={processing}
                    >
                      <FontAwesomeIcon icon={faEye} />
                      <span>Ver</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAprovar(documento)}
                      className="flex-1 sm:flex-none px-3 py-2 text-white bg-green-600 hover:bg-green-700 rounded flex items-center justify-center gap-1 disabled:opacity-50 text-sm"
                      disabled={processing}
                    >
                      <FontAwesomeIcon icon={faCheck} />
                      <span>Aprovar</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => { setDocumentoToReject(documento); setShowRejectModal(true); }}
                      className="flex-1 sm:flex-none px-3 py-2 text-white bg-red-600 hover:bg-red-700 rounded flex items-center justify-center gap-1 disabled:opacity-50 text-sm"
                      disabled={processing}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                      <span>Rejeitar</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="mt-6">
              <Pagination currentPage={page} totalPages={totalPages} totalItems={total || 0} itemsPerPage={limit} onPageChange={setPage} />
            </div>
          )}
        </>
      )}

      {/* Modal de visualização */}
      {viewingDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-3 sm:p-4 border-b flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <h3 className="font-medium text-sm sm:text-base truncate">
                  {documentoAlunoService.getTipoLabel(viewingDocument.tipoDocumento)}
                </h3>
                <p className="text-xs sm:text-sm text-gray-500 truncate">Aluno: {viewingDocument.nomeAluno}</p>
              </div>
              <button
                onClick={() => { if (viewingDocument.blobUrl) URL.revokeObjectURL(viewingDocument.blobUrl); setViewingDocument(null); }}
                className="p-2 hover:bg-gray-100 rounded flex-shrink-0"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-2 sm:p-4 bg-gray-100">
              {viewingDocument.mimeType?.includes('pdf') ? (
                <iframe src={viewingDocument.blobUrl} className="w-full h-[50vh] sm:h-[70vh]" title="Visualização do documento" />
              ) : (
                <img src={viewingDocument.blobUrl} alt={viewingDocument.tipoDocumento} className="max-w-full h-auto mx-auto" />
              )}
            </div>
            <div className="p-3 sm:p-4 border-t flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
              <button
                type="button"
                onClick={async () => {
                  await handleAprovar(viewingDocument);
                  if (viewingDocument.blobUrl) URL.revokeObjectURL(viewingDocument.blobUrl);
                  setViewingDocument(null);
                }}
                className="w-full sm:w-auto px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded flex items-center justify-center gap-2"
                disabled={processing}
              >
                <FontAwesomeIcon icon={faCheck} />
                Aprovar
              </button>
              <button
                type="button"
                onClick={() => {
                  setDocumentoToReject(viewingDocument);
                  setShowRejectModal(true);
                  if (viewingDocument.blobUrl) URL.revokeObjectURL(viewingDocument.blobUrl);
                  setViewingDocument(null);
                }}
                className="w-full sm:w-auto px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded flex items-center justify-center gap-2"
                disabled={processing}
              >
                <FontAwesomeIcon icon={faTimes} />
                Rejeitar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de rejeição individual */}
      {showRejectModal && !batchAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-white rounded-lg w-full max-w-md p-4 sm:p-6">
            <h3 className="font-bold text-base sm:text-lg mb-4">Rejeitar Documento</h3>
            <p className="text-gray-600 text-sm mb-1"><strong>Aluno:</strong> {documentoToReject?.nomeAluno}</p>
            <p className="text-gray-600 text-sm mb-4">
              <strong>Documento:</strong> {documentoToReject ? documentoAlunoService.getTipoLabel(documentoToReject.tipoDocumento) : ''}
            </p>
            <label className="block text-sm font-medium mb-2">Motivo da rejeição *</label>
            <textarea
              value={motivoRejeicao}
              onChange={(e) => setMotivoRejeicao(e.target.value)}
              className="w-full border rounded px-3 py-2 h-24 focus:outline-none focus:ring-2 focus:ring-red-400 text-sm"
              placeholder="Informe o motivo da rejeição..."
            />
            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 mt-4">
              <button
                type="button"
                onClick={() => { setShowRejectModal(false); setDocumentoToReject(null); setMotivoRejeicao(''); }}
                className="w-full sm:w-auto px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 order-2 sm:order-1"
                disabled={processing}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleRejeitar}
                disabled={!motivoRejeicao.trim() || processing}
                className={`w-full sm:w-auto px-4 py-2 rounded text-white flex items-center justify-center gap-2 order-1 sm:order-2 ${
                  motivoRejeicao.trim() && !processing ? 'bg-red-600 hover:bg-red-700' : 'bg-red-300 cursor-not-allowed'
                }`}
              >
                {processing ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faTimes} />}
                Rejeitar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmação em lote */}
      {showBatchConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-white rounded-lg w-full max-w-md p-4 sm:p-6">
            <h3 className="font-bold text-base sm:text-lg mb-4">
              {batchAction === 'aprovar' ? 'Aprovar Documentos em Lote' : 'Rejeitar Documentos em Lote'}
            </h3>
            
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-sm text-yellow-800">
                <strong>{selectedIds.size}</strong> documento(s) serão {batchAction === 'aprovar' ? 'aprovados' : 'rejeitados'}.
              </p>
            </div>
            
            {batchAction === 'rejeitar' && (
              <>
                <label className="block text-sm font-medium mb-2">Motivo da rejeição (será aplicado a todos) *</label>
                <textarea
                  value={motivoRejeicao}
                  onChange={(e) => setMotivoRejeicao(e.target.value)}
                  className="w-full border rounded px-3 py-2 h-24 focus:outline-none focus:ring-2 focus:ring-red-400 text-sm mb-4"
                  placeholder="Informe o motivo da rejeição..."
                />
              </>
            )}
            
            {processing && (
              <div className="mb-4">
                <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                  <span>Processando...</span>
                  <span>{batchProgress.current} / {batchProgress.total}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all ${batchAction === 'aprovar' ? 'bg-green-600' : 'bg-red-600'}`}
                    style={{ width: `${(batchProgress.current / batchProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}
            
            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
              <button
                type="button"
                onClick={() => { setShowBatchConfirm(false); setBatchAction(null); setMotivoRejeicao(''); }}
                className="w-full sm:w-auto px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 order-2 sm:order-1"
                disabled={processing}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={batchAction === 'aprovar' ? handleBatchAprovar : handleBatchRejeitar}
                disabled={processing || (batchAction === 'rejeitar' && !motivoRejeicao.trim())}
                className={`w-full sm:w-auto px-4 py-2 rounded text-white flex items-center justify-center gap-2 order-1 sm:order-2 ${
                  batchAction === 'aprovar' 
                    ? 'bg-green-600 hover:bg-green-700 disabled:bg-green-300' 
                    : 'bg-red-600 hover:bg-red-700 disabled:bg-red-300'
                } disabled:cursor-not-allowed`}
              >
                {processing ? (
                  <><FontAwesomeIcon icon={faSpinner} spin /> Processando...</>
                ) : (
                  <><FontAwesomeIcon icon={batchAction === 'aprovar' ? faCheckDouble : faTimes} /> {batchAction === 'aprovar' ? 'Aprovar Todos' : 'Rejeitar Todos'}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentosPendentes;
